import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import * as React from 'react'
import { createImpressionViewAncillariesEvent } from '@/scopes/checkout/domain/analytics/index'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { useGetAllCarsQuery } from '@/scopes/checkout/filterableCars/domain/hooks'
import { useAvailableRoomsQuery } from '@/scopes/checkout/groups/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'

export function useSendImpressionViewAncillaries() {
  const { sendEvent } = useAnalytics()
  const { isLoading: isDraftLoading, data: draftData } = useBookingDraftQuery()
  const hasCarRentalComponent = !!draftData?.services?.carRentalFiltering
  const { isLoading: isCarsLoading, data: carsData } = useGetAllCarsQuery({ enabled: hasCarRentalComponent })
  const isGroupAndHasRoomEnabled = !!draftData?.groupInformation && !!draftData?.groupInformation.roomSelectionIsEnabled
  const { isLoading: isRoomsLoading, data: roomsGA4Items } = useAvailableRoomsQuery({ enabled: isGroupAndHasRoomEnabled })
  const logger = React.useMemo(() => createNewRelicLogger({ scope: 'checkout' }), [])
  const hasBeenSent = React.useRef(false)

  React.useEffect(() => {
    if (isDraftLoading || isCarsLoading || isRoomsLoading)
      return
    if (hasBeenSent.current)
      return
    if (!draftData?.analytics || !draftData?.ga4AncillaryImpressions)
      return

    const serviceItems = draftData.ga4AncillaryImpressions

    const carItems: ImpressionViewAncillariesItem[] = carsData?.cars
      ? Object.values(carsData.cars).map(car => ({
          item_name: car.label,
          item_id: car.value,
          price: car.price,
          item_category: 'Ancillary > Additional cost > carRentalFiltering',
          item_category2: 'carRentalFiltering',
          item_list_name: 'checkout1',
          index: 0,
          quantity: '1',
        }))
      : []

    const roomsItems: ImpressionViewAncillariesItem[] = roomsGA4Items?.ga4Impressions ?? []

    const allItems = [...roomsItems, ...serviceItems, ...carItems].map((item, i) => ({
      ...item,
      index: i + 1,
    }))

    const event = createImpressionViewAncillariesEvent(allItems, draftData.analytics)
    sendEvent(event)
    logger.setContext('draftId', draftData.draftId)
    logger.logEvent('checkout_datalayer_push', { ...event, ecommerce: JSON.stringify(event.ecommerce) })

    hasBeenSent.current = true
  }, [isDraftLoading, isCarsLoading, isRoomsLoading, draftData, carsData, roomsGA4Items, sendEvent, logger])
}
